import { FormControlLabel } from '@mui/material';
import { AlertAccentWrapper } from '@shared/components/core/alertNotification/CommonAlertNotificationStyles';
import { FormGroup, Switch } from '@shared/components/core/field/CommonFieldStyles';
import Icons from '@shared/components/core/images/Icons';
import { BodyMedium, BodySmall, TitleSmall, TitleXSmall } from '@shared/components/core/text/Typography';
import OrganizationMemberRole, {
    OrganizationMemberRoleKeys,
} from '@shared/models/organizations/OrganizationMemberRole';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

interface IRoleEditorProps {
    myRole: OrganizationMemberRole;
    title?: string;
    titleDescription?: string;
    role: OrganizationMemberRole;
    onChange: (role: OrganizationMemberRole) => void;
    includeDescription?: boolean;
}

function RoleEditor({
    myRole,
    role,
    title,
    titleDescription,
    includeDescription = true,
    onChange,
}: Readonly<IRoleEditorProps>) {
    const { t } = useTranslation();
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;
        onChange({
            ...role,
            [name]: checked,
        });
    };
    const accessibleRoles = Object.keys(role).filter((key) => {
        const typedKey = key as OrganizationMemberRoleKeys;
        return myRole.isAdmin || myRole[typedKey];
    });

    if (!myRole.isAdmin && role.isAdmin)
        return (
            <AlertAccentWrapper $color="default">
                <Icons name="warning" />
                <BodyMedium>{t('common:organization.isAdmin.conflict')}</BodyMedium>
            </AlertAccentWrapper>
        );

    return (
        <FormGroup>
            {title && <TitleSmall>{title}</TitleSmall>}
            {titleDescription && <BodyMedium>{titleDescription}</BodyMedium>}

            {accessibleRoles.map((key) => {
                const typedKey = key as OrganizationMemberRoleKeys;
                return (
                    <FormControlLabel
                        key={key}
                        control={
                            <Switch
                                disabled={typedKey !== 'isAdmin' && role.isAdmin}
                                checked={role.isAdmin === true ? role.isAdmin : role[typedKey]}
                                onChange={handleChange}
                                name={typedKey}
                            />
                        }
                        label={
                            <BodySmall>
                                {includeDescription ? (
                                    <>
                                        <TitleXSmall as="span">
                                            {t(`common:organization.${typedKey}.title`)}
                                        </TitleXSmall>
                                        {': '}
                                        {t(`common:organization.${typedKey}.description`)}
                                    </>
                                ) : (
                                    t(`common:organization.${typedKey}.title`)
                                )}
                            </BodySmall>
                        }
                    />
                );
            })}
        </FormGroup>
    );
}

export default RoleEditor;
